/**
 * Resolver Hooks for External Components
 * Allows external components to resolve CDS tokens to usable values
 */

import { useMemo } from "react";
import { useColorMode } from "@chakra-ui/react";
import { useColorModeValue } from "./useColorModeValue";
import { resolveSpacing } from "../foundation";
import {
  TextColorToken,
  SurfaceColorToken,
  BorderColorToken,
  InteractiveColorToken,
  PrimitiveColorToken,
} from "../foundation/Color";
import { SpacingToken } from "../foundation/Spacing";
import { SizeToken, resolveSize } from "../foundation/Size";
import { ShadowToken, resolveShadow } from "../foundation/Shadow";
import { RadiusToken, resolveRadius } from "../foundation/Radius";

// Union type for all color tokens
type ColorToken = TextColorToken | SurfaceColorToken | BorderColorToken | InteractiveColorToken | PrimitiveColorToken;

/**
 * Hook to resolve any CDS color token to a CSS color value
 * Usage: const { value } = useResolveColor(CDS.Color.surface.primary)
 */
export const useResolveColor = (token: ColorToken) => {
  const resolvedColor = useColorModeValue(token);

  return useMemo(() => ({
    value: resolvedColor,
  }), [resolvedColor]);
};

/**
 * Hook to resolve any CDS spacing token to a CSS spacing value
 * Usage: const { value } = useResolveSpacing(CDS.Spacing.medium)
 */
export const useResolveSpacing = (token: SpacingToken) => {
  const resolvedSpacing = useMemo(() => resolveSpacing(token), [token]);

  return useMemo(() => ({
    value: resolvedSpacing,
  }), [resolvedSpacing]);
};

/**
 * Hook to resolve any CDS size token to a CSS size value
 * Usage: const { value } = useResolveSize(CDS.Size.percent(100))
 */
export const useResolveSize = (token: SizeToken) => {
  const resolvedSize = useMemo(() => resolveSize(token), [token]);

  return useMemo(() => ({
    value: resolvedSize,
  }), [resolvedSize]);
};

/**
 * Hook to resolve any CDS shadow token to a CSS shadow value
 * Usage: const { value } = useResolveShadow(CDS.Shadow.card)
 */
export const useResolveShadow = (token: ShadowToken) => {
  const { colorMode } = useColorMode();
  const isDarkMode = colorMode === "dark";

  const resolvedShadow = useMemo(() => resolveShadow(token, isDarkMode), [token, isDarkMode]);

  return useMemo(() => ({
    value: resolvedShadow,
  }), [resolvedShadow]);
};

/**
 * Hook to resolve any CDS radius token to a CSS border-radius value
 * Usage: const { value } = useResolveRadius(CDS.Radius.button)
 */
export const useResolveRadius = (token: RadiusToken) => {
  const resolvedRadius = useMemo(() => resolveRadius(token), [token]);

  return useMemo(() => ({
    value: resolvedRadius,
  }), [resolvedRadius]);
};

